import React, { useEffect, useState } from 'react';
import CardHeader from 'components/v2/card/CardHeader';
import CardFooter from 'components/v2/card/CardFooter';
import AddButton from 'components/v2/buttons/AddButton';
import DeleteButton from 'components/v2/buttons/DeleteButton';
import Button from 'components/v2/buttons/Button';
import Label from 'components/v2/forms/Label';
import Panel from 'components/v2/Panel';
import PanelSection from 'components/v2/PanelSection';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { findUsers } from 'utils/UserWebAPIUtils';
import {
  alreadyConfirmedLaneRates,
  updateLaneRates,
} from 'utils/AccessorialRequestWebAPIUtils';
import { showToast } from 'stores/ToastStore';
import { validateDocumentFilesSizes, isPDFEncrypted } from 'utils/documents';
import moment from 'moment-timezone';
import { FileUploader } from 'components/v2/FileUploader';
import {
  createErrorMessage,
  laneRatesMapper,
} from 'components/modules/pricing/lane-rates-v2/lane-rates-v2-helpers';
import Alert from 'components/ui/Alert';
import { omitBy, isNil } from 'lodash';
import LaneRateForm from './LaneRateForm';

export default function LaneRatesEditor({
  className,
  laneRates: laneRatesProp,
  isActionCreate,
}) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(false);
  const [savingFscConfigs, setSavingFscConfigs] = useState([]);
  const history = useHistory();
  const [files, setFiles] = useState([]);
  const [confirmed, setConfirmed] = useState(false);
  const [laneRates, setLaneRates] = useState(
    laneRatesProp ? laneRatesMapper(laneRatesProp) : [{}],
  );
  const location = useLocation();
  const [fileRequiredError, setFileRequiredError] = useState(false);
  const [fileEncryptedError, setFileEncryptedError] = useState(false);
  const [saveErrors, setSaveErrors] = useState([]);
  const [formValid, setFormValid] = useState(true);

  const action = isActionCreate ? 'create' : 'update';

  useEffect(() => {
    validateFileUpload(files).then((valid) =>
      setSaveDisabled(!valid || !formValid),
    );
  }, [files]);

  useEffect(() => {
    if (!saving) {
      return;
    }

    const allSaved = savingFscConfigs.every((saving) => !saving);

    if (allSaved && saving) {
      continueSaving();
    }
  }, [savingFscConfigs]);

  const handleSave = async () => {
    setSaveErrors([]);
    if (saving) {
      return;
    }

    setSaving(true);
    setSavingFscConfigs(laneRates.map(() => true));

    // continueSaving() will be called after all fsc configs are saved
    // savingFscConfigs[] state triggers child forms to validate form then save FSC configs then call onSaveSuccess or onSaveFail
  };

  const continueSaving = async () => {
    if (!saving) {
      return;
    }

    if (confirmed) {
      try {
        validateDocumentFilesSizes(files);
      } catch (e) {
        console.error('Error validating document files sizes', e);
        return;
      }
    } else {
      laneRates.map((laneRate) => {
        delete laneRate.document;
        return {
          ...laneRate,
        };
      });
    }

    let laneRatesToSave = await addCustomerEmails(laneRates);

    // Change lane rate objects format to fit old API schema
    laneRatesToSave = laneRatesToSave.map((laneRate) => {
      const newLaneRate = {
        ...laneRate,
        alreadyConfirmed: confirmed,
        reversible: laneRate.reversible ? 'Yes' : 'No',
      };

      // Change date format to send like in old flow
      newLaneRate.effectiveStart = moment
        .utc(newLaneRate.effectiveStart)
        .format();
      newLaneRate.effectiveEnd = moment.utc(newLaneRate.effectiveEnd).format();

      return newLaneRate;
    });

    try {
      const response = confirmed
        ? await alreadyConfirmedLaneRates(laneRatesToSave, files, action)
        : await updateLaneRates(laneRatesToSave, action);

      if (response?.entity?.success || response?.body?.success) {
        showToast(
          t('REQUEST_SUCCESS'),
          t('LANE_RATE_SUBMIT_SUCCESS'),
          'success',
        );
        goToLaneRates();
      } else {
        showToast(t('REQUEST_ERROR'), t('LANE_RATE_UPDATE_ERROR'), 'error');
        setSaving(false);
      }
    } catch (error) {
      setSaveErrors(error.body?.validationErrors);

      showToast(t('REQUEST_ERROR'), t('LANE_RATE_UPDATE_ERROR'), 'error');
      setSaving(false);
    }
  };

  const addCustomerEmails = async (laneRates) => {
    const customerIds = laneRates.map((laneRate) => laneRate.customerId);
    const findUsersResponse = await findUsers({ _id: { $in: customerIds } });
    const customers = findUsersResponse?.entity?.items;

    if (!customers) {
      return laneRates;
    }

    return laneRates.map((laneRate) => {
      const customer = customers.find(
        (customer) => customer.id === laneRate.customer,
      );
      return {
        ...laneRate,
        customerEmail: customer?.email,
      };
    });
  };

  const renderHeader = () => {
    const title =
      location.pathname === '/lane-rates/add'
        ? t('ADD_LANE_RATE')
        : t('EDIT_LANE_RATES');

    return (
      <CardHeader title={title}>
        {isActionCreate && (
          <AddButton
            size="small"
            dataTestId="lane-rate-add-edit-button"
            onClick={handleAddLaneRate}
          />
        )}
      </CardHeader>
    );
  };

  const uploadConfirmationDocuments = () => {
    if (confirmed) {
      return null;
    }

    return (
      <Button
        color="green"
        onClick={() => setConfirmed(true)}
        testDataId="lane-rate-save-and-upload-confirmation"
      >
        {t('SAVE_AND_UPLOAD_CONFIRMATION')}
      </Button>
    );
  };

  const onCancelClick = () => {
    setFileRequiredError(false);
    return confirmed ? setConfirmed(false) : goToLaneRates();
  };

  const renderFooter = () => {
    const actionLabel = t('SAVE_AND_COMPLETE_UPLOAD_CONFIRMATION');

    return (
      <div className="flex items-center justify-end space-x-4">
        {uploadConfirmationDocuments()}
        {confirmed && (
          <Button
            color="green"
            onClick={handleSave}
            testDataId={`lane-rate-${actionLabel}`}
            disabled={saveDisabled}
          >
            {actionLabel}
          </Button>
        )}
        <Button
          color=""
          onClick={onCancelClick}
          testDataId="lane-rate-cancel-button"
        >
          {t('CANCEL')}
        </Button>
      </div>
    );
  };

  const goToLaneRates = () => {
    const listQueryFilterParameters = location.state?.listQueryFilterParameters;
    if (listQueryFilterParameters) {
      history.push('/lane-rates', { listQueryFilterParameters });
    } else {
      history.push('/lane-rates');
    }
  };

  const handleAddLaneRate = () => {
    setLaneRates([...laneRates, { name: t('NEW_LANE_RATE') }]);
  };

  const handleRemoveLaneRate = (i) => {
    setLaneRates([...laneRates.slice(0, i), ...laneRates.slice(i + 1)]);
  };

  const handleChangeLaneRate = (index, laneRate) => {
    const newLaneRates = [...laneRates];
    const newLaneRate = omitBy(laneRate, isNil);
    newLaneRates[index] = newLaneRate;
    setLaneRates(newLaneRates);
    setSaveErrors([]);
  };

  const validateFileUpload = async () => {
    if (confirmed && files.length === 0) {
      setFileRequiredError(true);
      return false;
    }

    const anyFilesEncryptedPDFs = (
      await Promise.all(files.map(isPDFEncrypted))
    ).some((isEncrypted) => isEncrypted);

    if (anyFilesEncryptedPDFs) {
      setFileEncryptedError(true);
      return false;
    }

    setFileRequiredError(false);
    setFileEncryptedError(false);

    return true;
  };

  const handleValidation = async (index, valid) => {
    const filesValid = await validateFileUpload();
    setFormValid(valid);
    if (!valid || !filesValid) {
      setSaving(false);
      setSavingFscConfigs([]);
      setSaveDisabled(true);
      return;
    }

    setSaveDisabled(false);

    if (!savingFscConfigs[index]) {
      return;
    }

    setSavingFscConfigs((prevState) => {
      const newSavingFscConfigs = [...prevState];
      newSavingFscConfigs[index] = false;
      return newSavingFscConfigs;
    });
  };

  const renderFileInput = () => {
    return (
      <PanelSection>
        <Label className="mb-4">{t('UPLOAD_CONFIRMATION')}</Label>

        <FileUploader
          onChange={(files) => {
            setFiles(files);
            setFileRequiredError(false);
            setFileEncryptedError(false);
          }}
          fileTypesLabel={`${t('PDF')}, ${t('IMAGE')}`}
          multiple
        />

        {fileRequiredError && (
          <span
            data-testid="errorLabelFile"
            className="block text-xs italic text-red-500 mt-1"
          >
            {t('FILE_REQUIRED')}
          </span>
        )}
        {fileEncryptedError && (
          <span
            data-testid="errorLabelFile"
            className="block text-xs italic text-red-500 mt-1"
          >
            {t('PDF_DOCUMENT_ENCRYPTED')}
          </span>
        )}
      </PanelSection>
    );
  };

  const renderSaveErrors = (saveErrors) => {
    if (!saveErrors || !saveErrors.length) {
      return null;
    }
    return (
      <Alert
        type="error"
        title={`${t('PLEASE_ADDRESS_THE_FOLLOWING_ISSUES')}`}
        canClose
        onClose={() => setSaveErrors([])}
        className="mb-4"
      >
        {saveErrors.map((error, i) => {
          return (
            <div key={`save-errors-${i}`}>{createErrorMessage(error)}</div>
          );
        })}
      </Alert>
    );
  };

  return (
    <div className="w-full">
      <Panel className={className}>
        {renderHeader()}
        {renderSaveErrors(saveErrors)}
        <div className="divide-y divide-gray-200">
          {laneRates.map((laneRate, i) => (
            <PanelSection className="py-4" key={i}>
              <div className="mb-4 flex items-center space-x-3">
                <h3 className="">
                  {/* Increase by one to set human-understandable value */}
                  {t('LANE_RATE')} {i + 1}
                </h3>
                {i !== 0 && (
                  <DeleteButton
                    onClick={() => handleRemoveLaneRate(i)}
                    size="small"
                  />
                )}
              </div>
              <LaneRateForm
                isActionCreate={isActionCreate}
                initialLaneRates={laneRatesProp}
                laneRate={laneRate}
                onChange={(laneRate) => handleChangeLaneRate(i, laneRate)}
                saving={saving}
                onValidate={(valid) => handleValidation(i, valid)}
              />
            </PanelSection>
          ))}

          {confirmed && renderFileInput()}
        </div>
        <div className="bg-gray-50 px-4 py-5">{renderFooter()}</div>
      </Panel>
    </div>
  );
}
